import React, { useState, useEffect } from 'react';
import { requestOnboarding } from '../utils/onboarding';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Button,
  Alert,
  Snackbar,
  Grid,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  Checkbox,
  FormGroup,
  useTheme,
  Switch,
  Slider,
  // IconButton — only used by the disabled certification card photo section
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
// PhotoCamera and Delete are only used by the disabled certification card photo section
import { RestartAlt, Check, ScubaDiving, Air, DeleteSweep, Mail, People, Waves, Build, AccountCircle, ManageAccounts, ExpandMore, Download, Visibility, Event as EventIcon, Notifications } from '@mui/icons-material';
import BlockedAccountsPanel from './BlockedAccountsPanel';
import { OptionsManager, AppOptions, getCertificationDisplayName, getCertificationDescription, getHighestRecreationalCertification, getGasCertificationDisplayName, getGasCertificationDescription, hasAnyGasCertification, getSpecialtyCertificationDisplayName, getSpecialtyCertificationDescription, hasAnySpecialtyCertification, getAllSpecialtyCertifications, getFreediveCertificationDisplayName, getFreediveCertificationDescription, getFreediveCertificationMaxDepth, hasAnyFreediveCertification, getHighestFreediveCertification, getFreediveSpecialtyDisplayName, getFreediveSpecialtyDescription, getAllFreediveSpecialtyCertifications, getTimeFormatDisplayName, getDateFormatDisplayName, formatTime, formatDate, getThemeDisplayName, getThemeDescription, getThemeIcon, normalizePreviousDiveCount, MAX_PREVIOUS_DIVE_COUNT, getPrivacySettingDisplayName, getPrivacySettingDescription, BooleanPrivacySettingKey, DIVE_COUNT_DISPLAY_OPTIONS, DIVE_COUNT_RANGE_LABELS, getDiveCountRangeLabel } from '../utils/optionsManager';
import { buddyService } from '../services/buddyService';
// Only used by the disabled certification card photo section
// import { compressImageToDataUrl } from '../utils/imageUtils';
import { geocodingService } from '../services/geocodingService';
import { preferencesManager } from '../utils/preferencesManager';
import { authService, UserDto } from '../services/authService';
import UpdateProfileDialog from './UpdateProfileDialog';
import { ENABLE_GAS_ANALYTICS } from '../config/featureFlags';

// Photos are stored with user preferences (DynamoDB 400KB item limit), so cap the count
// const MAX_CARD_PHOTOS = 3;

/**
 * Collapsible sub-section of a settings card. Collapsed by default so the card
 * stays scannable — the optional header badge shows how many items are selected
 * inside, so a diver can see what they've filled in without expanding everything.
 */
const CollapsibleSection: React.FC<{
  title: string;
  subtitle: string;
  heldCount?: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, heldCount, defaultExpanded = false, children }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      '&:before': { display: 'none' },
      '&.Mui-expanded': { margin: 0 },
    }}
  >
    <AccordionSummary expandIcon={<ExpandMore />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
        {title}
      </Typography>
      {heldCount !== undefined && heldCount > 0 && (
        <Chip label={heldCount} size="small" color="primary" sx={{ height: 20, minWidth: 20 }} />
      )}
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
        {subtitle}
      </Typography>
      {children}
    </AccordionDetails>
  </Accordion>
);

export type ProfileSettingsTab = 'certifications' | 'preferences' | 'notifications' | 'privacy' | 'account';

interface ProfileSettingsTabsProps {
  activeTab: ProfileSettingsTab;
}

const ProfileSettingsTabs: React.FC<ProfileSettingsTabsProps> = ({ activeTab }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [options, setOptions] = useState<AppOptions | null>(null);
  const [logLevel, setLogLevel] = useState<string>('Warning');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [showCacheClearedMessage, setShowCacheClearedMessage] = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [notifBuddyRequests, setNotifBuddyRequests] = useState(true);
  // Opt-in: one logged dive notifies every buddy, so this defaults to off.
  const [notifBuddyActivity, setNotifBuddyActivity] = useState(false);
  const [notifWeeklyDigest, setNotifWeeklyDigest] = useState(false);
  // Opt-out: someone who RSVPed "Going" expects the reminder, so only an explicit false turns it off.
  // Must match EventReminders.IsOptedOut in the service-reminder Lambda.
  const [notifEventReminders, setNotifEventReminders] = useState(true);
  const [notifComeBack, setNotifComeBack] = useState(true);
  const [notifEventSuggestions, setNotifEventSuggestions] = useState(true);
  const [notifOnboardingTips, setNotifOnboardingTips] = useState(true);
  // Defaults on — a shared dive is a direct request for a response, not a fan-out
  const [notifDiveShares, setNotifDiveShares] = useState(true);
  const [notifEquipmentService, setNotifEquipmentService] = useState(true);
  const [serviceReminderDays, setServiceReminderDays] = useState(30);
  const [locationDisplayPreference, setLocationDisplayPreference] = useState<'geotagged' | 'description'>('geotagged');
  // Card photo state — disabled with the section below
  // const [cardPhotoError, setCardPhotoError] = useState<string | null>(null);
  // const [isUploadingCardPhoto, setIsUploadingCardPhoto] = useState(false);
  // Held as text so the field stays editable mid-typing (empty = not specified)
  const [previousDiveCountInput, setPreviousDiveCountInput] = useState('');
  const [accountUser, setAccountUser] = useState<UserDto | null>(authService.getUser());
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // Only used to suggest a sensible default bucket for diveCountRangeOverride —
  // the diver's real count, fetched lazily since most settings don't need it.
  const [ownDiveCount, setOwnDiveCount] = useState<number | null>(null);
  const optionsManager = OptionsManager.getInstance();

  // Helper function to get userId - tries preferencesManager first, then authService
  const getUserId = (): string | null => {
    let userId = preferencesManager.getUserId();
    if (!userId) {
      const user = authService.getUser();
      if (user) {
        preferencesManager.setUserId(user.userId);
        userId = user.userId;
      }
    }
    return userId;
  };

  useEffect(() => {
    // Load initial options
    const initialOptions = optionsManager.getOptions();
    setOptions(initialOptions);
    setPreviousDiveCountInput(
      initialOptions.diver.previousDiveCount != null ? String(initialOptions.diver.previousDiveCount) : ''
    );

    // Subscribe to options changes
    // Note: Initial preference loading from database happens via usePreferencesSync,
    // called from DiveExplorer/UserProfile at app entry points.
    const unsubscribe = optionsManager.subscribe((newOptions) => {
      setOptions(newOptions);
    });

    return unsubscribe;
  }, [optionsManager]);

  const syncedPreviousDiveCount = options?.diver.previousDiveCount;
  useEffect(() => {
    // Adopt a previous dive count that arrived from the database sync; the field
    // already shows what the diver typed, so only correct it when it disagrees
    if (syncedPreviousDiveCount == null) return;
    setPreviousDiveCountInput((current) =>
      normalizePreviousDiveCount(current) === syncedPreviousDiveCount ? current : String(syncedPreviousDiveCount)
    );
  }, [syncedPreviousDiveCount]);

  useEffect(() => {
    // Load email notification preferences from authService user data
    const user = authService.getUser();
    if (user) {
      setEmailNotificationsEnabled(user.emailNotificationsEnabled ?? true);
      setServiceReminderDays(user.serviceReminderDays ?? 30);
    }
    // Load buddy notification prefs from preferencesManager customSettings.
    // Subscribed rather than read once: preferences load asynchronously, so on a
    // direct visit to /settings the cache is still empty at mount and these
    // toggles would render defaults instead of what the diver actually saved.
    const applyNotificationPrefs = (custom: Record<string, any>) => {
      if ('notifBuddyRequests' in custom) setNotifBuddyRequests(custom.notifBuddyRequests);
      // Opt-in: only a stored `true` enables it, so an absent or malformed value stays off.
      setNotifBuddyActivity(custom.notifBuddyActivity === true);
      setNotifWeeklyDigest(custom.notifWeeklyDigest === true);
      setNotifEventReminders(custom.notifEventReminders !== false);
      setNotifComeBack(custom.notifComeBack !== false);
      setNotifEventSuggestions(custom.notifEventSuggestions !== false);
      setNotifOnboardingTips(custom.notifOnboardingTips !== false);
      // Opt-out, unlike buddy activity: a share needs a response, so only an explicit
      // `false` turns it off. This must match the backend default in DiveSharesController.
      setNotifDiveShares(custom.notifDiveShares !== false);
      if ('notifEquipmentService' in custom) setNotifEquipmentService(custom.notifEquipmentService);
    };

    const loaded = preferencesManager.getPreferences();
    if (loaded) {
      applyNotificationPrefs(loaded.customSettings ?? {});
    }

    return preferencesManager.subscribe((prefs) => {
      applyNotificationPrefs(prefs.customSettings ?? {});
    });
  }, []);

  useEffect(() => {
    // Keep the Account card in sync when auth state changes anywhere in the app
    return authService.subscribe(() => setAccountUser(authService.getUser()));
  }, []);

  useEffect(() => {
    // Fetches the diver's own real dive count once, purely to suggest a sensible
    // default bucket in the range-override picker — never shown or saved directly.
    const userId = getUserId();
    if (!userId) return;
    buddyService.getUserProfile(userId)
      .then((profile) => setOwnDiveCount(profile.diveCount ?? 0))
      .catch(() => setOwnDiveCount(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Load and subscribe to location display preference changes
    const currentPref = preferencesManager.getPreferences()?.locationDisplayPreference || 'geotagged';
    setLocationDisplayPreference(currentPref);

    const unsubscribe = preferencesManager.subscribe((prefs) => {
      setLocationDisplayPreference(prefs.locationDisplayPreference || 'geotagged');
    });

    return unsubscribe;
  }, []);

  const handleUnitChange = async (category: keyof AppOptions['units'], value: string) => {
    if (!options) return;

    const newUnits = { ...options.units, [category]: value };

    optionsManager.updateCategory('units', newUnits);

    // Save ALL unit preferences to database (not just the one that changed)
    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        const updates: any = {
          // Save all unit preferences
          showDepthInMeters: newUnits.depth === 'meters',
          showTemperatureInCelsius: newUnits.temperature === 'celsius',
          showPressureInBar: newUnits.pressure === 'bar',
          // Also update preferredUnit for overall system preference
          preferredUnit: (newUnits.depth === 'meters' && newUnits.temperature === 'celsius' && newUnits.pressure === 'bar') ? 'metric' : 'imperial',
          // Weight has no dedicated column — carry it in customSettings
          customSettings: {
            ...currentPrefs?.customSettings,
            weightUnit: newUnits.weight
          }
        };

        await preferencesManager.savePreferences(updates);
        console.log('Options: Saved all unit preferences to database:', updates);
      }
    } catch (error) {
      console.error('Failed to save unit preferences to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleTankSizeChange = async (value: string) => {
    if (!options) return;

    optionsManager.updateCategory('equipment', {
      ...options.equipment,
      tankSize: value as AppOptions['equipment']['tankSize']
    });

    // Save tank size to database in customSettings
    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            tankSize: value
          }
        });
      }
    } catch (error) {
      console.error('Failed to save tank size to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleCustomTankVolumeChange = async (value: string) => {
    if (!options) return;

    const volume = parseFloat(value);
    if (isNaN(volume) || volume <= 0) return;

    optionsManager.updateCategory('equipment', {
      ...options.equipment,
      customTankVolume: volume
    });

    // Save custom tank volume to database in customSettings
    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            customTankVolume: volume
          }
        });
      }
    } catch (error) {
      console.error('Failed to save custom tank volume to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleGasConsumptionMetricChange = async (value: string) => {
    if (!options) return;

    optionsManager.updateCategory('display', {
      ...options.display,
      gasConsumptionMetric: value as AppOptions['display']['gasConsumptionMetric']
    });

    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            gasConsumptionMetric: value
          }
        });
      }
    } catch (error) {
      console.error('Failed to save gas consumption metric to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleRecreationalCertificationChange = async (certKey: keyof AppOptions['diver']['recreationalCertifications'], checked: boolean) => {
    if (!options) return;

    const updatedRecCerts = {
      ...options.diver.recreationalCertifications,
      [certKey]: checked
    };
    // Keep the legacy single highest-level value in sync for downstream consumers
    const highest = getHighestRecreationalCertification(updatedRecCerts);

    optionsManager.updateCategory('diver', {
      ...options.diver,
      recreationalCertifications: updatedRecCerts,
      certification: highest
    });

    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          certification: highest,
          customSettings: {
            ...currentPrefs?.customSettings,
            recreationalCertifications: updatedRecCerts
          }
        });
      }
    } catch (error) {
      console.error('Failed to save recreational certifications to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handlePreviousDiveCountChange = async (rawValue: string) => {
    // Keep only digits so the stored count can't pick up stray characters, and clamp
    // to the cap so the field never displays a value that wasn't actually saved
    const digitsOnly = rawValue.replace(/[^0-9]/g, '');
    const clamped = digitsOnly === ''
      ? ''
      : String(Math.min(Number(digitsOnly), MAX_PREVIOUS_DIVE_COUNT));
    setPreviousDiveCountInput(clamped);
    if (!options) return;

    // An empty field means "not specified" — distinct from an explicit 0
    const count = clamped === '' ? undefined : normalizePreviousDiveCount(clamped);

    optionsManager.updateCategory('diver', {
      ...options.diver,
      previousDiveCount: count
    });

    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            previousDiveCount: count ?? null
          }
        });
      }
    } catch (error) {
      console.error('Failed to save previous dive count to database:', error);
    }

    setShowSavedMessage(true);
  };

  /* Certification card photo handlers — temporarily disabled alongside the UI
     section below. See the note there: base64 photos in customSettings push the
     DynamoDB item toward its 400KB limit and break preference saves.
  const saveCardPhotos = async (photos: AppOptions['diver']['certificationCardPhotos']) => {
    if (!options) return;

    optionsManager.updateCategory('diver', {
      ...options.diver,
      certificationCardPhotos: photos
    });

    try {
      const userId = getUserId();
      if (userId) {
        await preferencesManager.savePreferences({
          customSettings: {
            certificationCardPhotos: photos
          }
        });
      }
    } catch (error) {
      console.error('Failed to save certification card photos to database:', error);
    }
  };

  const handleCardPhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file || !options) return;

    if (options.diver.certificationCardPhotos.length >= MAX_CARD_PHOTOS) {
      setCardPhotoError(`You can store up to ${MAX_CARD_PHOTOS} card photos — delete one first.`);
      return;
    }

    setCardPhotoError(null);
    setIsUploadingCardPhoto(true);
    try {
      const dataUrl = await compressImageToDataUrl(file);
      await saveCardPhotos([
        ...options.diver.certificationCardPhotos,
        { id: `card-${Date.now()}`, dataUrl, uploadedAt: new Date().toISOString() }
      ]);
      setShowSavedMessage(true);
    } catch (error) {
      console.error('Failed to process certification card photo:', error);
      setCardPhotoError('Could not process that image. Please try a different photo.');
    } finally {
      setIsUploadingCardPhoto(false);
    }
  };

  const handleCardPhotoDelete = async (photoId: string) => {
    if (!options) return;
    await saveCardPhotos(options.diver.certificationCardPhotos.filter((p) => p.id !== photoId));
    setShowSavedMessage(true);
  };
  */

  const handleLogLevelChange = async (value: string) => {
    setLogLevel(value);
    // logLevel is stored in optionsManager (localStorage), not in UserPreferences database
    setShowSavedMessage(true);
  };

  const handleGasCertificationChange = async (gasType: keyof AppOptions['diver']['gasCertifications'], checked: boolean) => {
    if (!options) return;

    optionsManager.updateCategory('diver', {
      ...options.diver,
      gasCertifications: {
        ...options.diver.gasCertifications,
        [gasType]: checked
      }
    });

    // Save gas certifications to database in customSettings
    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        const updatedGasCerts = {
          ...options.diver.gasCertifications,
          [gasType]: checked
        };
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            gasCertifications: updatedGasCerts
          }
        });
      }
    } catch (error) {
      console.error('Failed to save gas certifications to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleSpecialtyCertificationChange = async (certType: keyof AppOptions['diver']['specialtyCertifications'], checked: boolean) => {
    if (!options) return;

    optionsManager.updateCategory('diver', {
      ...options.diver,
      specialtyCertifications: {
        ...options.diver.specialtyCertifications,
        [certType]: checked
      }
    });

    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        const updatedSpecialtyCerts = {
          ...options.diver.specialtyCertifications,
          [certType]: checked
        };
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            specialtyCertifications: updatedSpecialtyCerts
          }
        });
      }
    } catch (error) {
      console.error('Failed to save specialty certifications to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleFreediveCertificationChange = async (certType: keyof AppOptions['diver']['freediveCertifications'], checked: boolean) => {
    if (!options) return;

    const updatedFreediveCerts = {
      ...options.diver.freediveCertifications,
      [certType]: checked
    };

    optionsManager.updateCategory('diver', {
      ...options.diver,
      freediveCertifications: updatedFreediveCerts
    });

    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            freediveCertifications: updatedFreediveCerts
          }
        });
      }
    } catch (error) {
      console.error('Failed to save freediving certifications to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleFreediveSpecialtyChange = async (certType: keyof AppOptions['diver']['freediveSpecialtyCertifications'], checked: boolean) => {
    if (!options) return;

    const updatedFreediveSpecialties = {
      ...options.diver.freediveSpecialtyCertifications,
      [certType]: checked
    };

    optionsManager.updateCategory('diver', {
      ...options.diver,
      freediveSpecialtyCertifications: updatedFreediveSpecialties
    });

    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            freediveSpecialtyCertifications: updatedFreediveSpecialties
          }
        });
      }
    } catch (error) {
      console.error('Failed to save freediving specialties to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handlePrivacyChange = async (key: keyof AppOptions['privacy'], value: AppOptions['privacy'][typeof key]) => {
    if (!options) return;

    const updatedPrivacy = { ...options.privacy, [key]: value };

    optionsManager.updateCategory('privacy', updatedPrivacy);

    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            privacy: updatedPrivacy
          }
        });
      }
    } catch (error) {
      console.error('Failed to save privacy settings to database:', error);
    }

    setShowSavedMessage(true);
  };

  /**
   * Export everything we hold for this diver as a JSON download. Built from the
   * live API rather than localStorage so the file reflects the account, not just
   * whatever this browser happens to have cached.
   */
  const handleExportData = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      const { diveService } = await import('../services/diveService');
      const dives = await diveService.getMyDives();

      const exported = {
        exportedAt: new Date().toISOString(),
        account: accountUser
          ? {
              userId: accountUser.userId,
              email: accountUser.email,
              firstName: accountUser.firstName,
              lastName: accountUser.lastName,
            }
          : null,
        preferences: optionsManager.getOptions(),
        serverPreferences: preferencesManager.getPreferences(),
        dives,
      };

      const blob = new Blob([JSON.stringify(exported, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `new-achievement-scuba-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export data:', error);
      setExportError('Could not build your export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleTimeFormatChange = async (value: string) => {
    if (!options) return;

    optionsManager.updateCategory('display', {
      ...options.display,
      timeFormat: value as AppOptions['display']['timeFormat']
    });

    // Save time format to database in customSettings
    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            timeFormat: value
          }
        });
      }
    } catch (error) {
      console.error('Failed to save time format to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleDateFormatChange = async (value: string) => {
    if (!options) return;

    optionsManager.updateCategory('display', {
      ...options.display,
      dateFormat: value as AppOptions['display']['dateFormat']
    });

    // Save date format to database in customSettings
    try {
      const userId = getUserId();
      if (userId) {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({
          customSettings: {
            ...currentPrefs?.customSettings,
            dateFormat: value
          }
        });
      }
    } catch (error) {
      console.error('Failed to save date format to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleLocationDisplayPreferenceChange = async (value: string) => {
    // Save location display preference to database
    try {
      const userId = getUserId();
      if (userId) {
        await preferencesManager.savePreferences({
          locationDisplayPreference: value as 'geotagged' | 'description'
        });
        setShowSavedMessage(true);
      }
    } catch (error) {
      console.error('Failed to save location display preference to database:', error);
    }
  };

  const handleThemeChange = async (value: string) => {
    if (!options) return;

    optionsManager.updateCategory('display', {
      ...options.display,
      theme: value as AppOptions['display']['theme']
    });

    // Save theme to database
    try {
      const userId = getUserId();
      if (userId) {
        await preferencesManager.savePreferences({
          theme: value
        });
      }
    } catch (error) {
      console.error('Failed to save theme to database:', error);
    }

    setShowSavedMessage(true);
  };

  const handleNotificationPreferencesChange = async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        console.error('No auth token found');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/preferences/notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          emailNotificationsEnabled,
          serviceReminderDays
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update notification preferences');
      }

      const data = await response.json();

      // Update the user in authService with the new preferences
      if (data.user) {
        authService.updateUser(data.user);
      }

      setShowSavedMessage(true);
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      alert('Failed to save notification preferences. Please try again.');
    }
  };

  const handleResetToDefaults = () => {
    optionsManager.resetToDefaults();
    setShowResetConfirm(false);
    setShowSavedMessage(true);
  };

  if (!options) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        Loading options...
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Display Units Section */}
        {activeTab === 'preferences' && (
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              color: 'primary.main'
            }}>
              📏 Display Units
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Choose your preferred units for dive data display
            </Typography>

            <Box display="flex" flexDirection="column" gap={3}>
              {/* Depth Units */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Depth
                </FormLabel>
                <RadioGroup
                  value={options.units.depth}
                  onChange={(e) => handleUnitChange('depth', e.target.value)}
                  row
                >
                  <FormControlLabel 
                    value="meters" 
                    control={<Radio />} 
                    label="Meters (m)" 
                  />
                  <FormControlLabel 
                    value="feet" 
                    control={<Radio />} 
                    label="Feet (ft)" 
                  />
                </RadioGroup>
              </FormControl>

              <Divider />

              {/* Temperature Units */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Temperature
                </FormLabel>
                <RadioGroup
                  value={options.units.temperature}
                  onChange={(e) => handleUnitChange('temperature', e.target.value)}
                  row
                >
                  <FormControlLabel 
                    value="celsius" 
                    control={<Radio />} 
                    label="Celsius (°C)" 
                  />
                  <FormControlLabel 
                    value="fahrenheit" 
                    control={<Radio />} 
                    label="Fahrenheit (°F)" 
                  />
                </RadioGroup>
              </FormControl>

              <Divider />

              {/* Pressure Units */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Pressure
                </FormLabel>
                <RadioGroup
                  value={options.units.pressure}
                  onChange={(e) => handleUnitChange('pressure', e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="bar"
                    control={<Radio />}
                    label="Bar"
                  />
                  <FormControlLabel
                    value="psi"
                    control={<Radio />}
                    label="PSI"
                  />
                </RadioGroup>
              </FormControl>

              <Divider />

              {/* Weight Units */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Weight
                </FormLabel>
                <RadioGroup
                  value={options.units.weight}
                  onChange={(e) => handleUnitChange('weight', e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="kg"
                    control={<Radio />}
                    label="Kilograms (kg)"
                  />
                  <FormControlLabel
                    value="lbs"
                    control={<Radio />}
                    label="Pounds (lbs)"
                  />
                </RadioGroup>
              </FormControl>

            </Box>
          </Paper>
        </Grid>
        )}

        {/* Display Preferences Section — paired with Display Units */}
        {activeTab === 'preferences' && (
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main'
            }}>
              🎨 Display Preferences
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Customize the appearance and format preferences for your interface
            </Typography>

            <Box display="flex" flexDirection="column" gap={3}>
              {/* Theme Selection */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1.5 }}>
                  Theme
                </FormLabel>

                <RadioGroup
                  value={options.display.theme}
                  onChange={(e) => handleThemeChange(e.target.value)}
                >
                  {/* Ocean palette group */}
                  <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1, mb: 0.5 }}>
                    🌊 Ocean
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, mb: 1.5 }}>
                    {(['aiDiveBotLight', 'aiDiveBotDark', 'aiDiveBotAuto'] as const).map((val) => (
                      <FormControlLabel
                        key={val}
                        value={val}
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <span>{getThemeIcon(val)}</span>
                            {getThemeDisplayName(val)}
                          </Box>
                        }
                        sx={{ mr: 2 }}
                      />
                    ))}
                  </Box>

                  {/* Magenta palette group */}
                  <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1, mb: 0.5 }}>
                    🌸 Magenta
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                    {(['light', 'dark', 'auto'] as const).map((val) => (
                      <FormControlLabel
                        key={val}
                        value={val}
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <span>{getThemeIcon(val)}</span>
                            {getThemeDisplayName(val)}
                          </Box>
                        }
                        sx={{ mr: 2 }}
                      />
                    ))}
                  </Box>
                </RadioGroup>

                <Box sx={{ backgroundColor: 'action.hover', p: 1.5, borderRadius: 1, mt: 1.5 }}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    {getThemeIcon(options.display.theme)} {getThemeDisplayName(options.display.theme)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getThemeDescription(options.display.theme)}
                  </Typography>
                </Box>
              </FormControl>

              <Divider />

              {/* Time Format */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Time Format
                </FormLabel>
                <RadioGroup
                  value={options.display.timeFormat}
                  onChange={(e) => handleTimeFormatChange(e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="12h"
                    control={<Radio />}
                    label={getTimeFormatDisplayName('12h')}
                  />
                  <FormControlLabel
                    value="24h"
                    control={<Radio />}
                    label={getTimeFormatDisplayName('24h')}
                  />
                </RadioGroup>
              </FormControl>

              <Divider />

              {/* Date Format */}
              <FormControl fullWidth>
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                  Date Format
                </FormLabel>
                <Select
                  value={options.display.dateFormat}
                  onChange={(e) => handleDateFormatChange(e.target.value)}
                  size="small"
                >
                  <MenuItem value="MM/DD/YYYY">{getDateFormatDisplayName('MM/DD/YYYY')}</MenuItem>
                  <MenuItem value="DD/MM/YYYY">{getDateFormatDisplayName('DD/MM/YYYY')}</MenuItem>
                  <MenuItem value="YYYY-MM-DD">{getDateFormatDisplayName('YYYY-MM-DD')}</MenuItem>
                  <MenuItem value="DD MMM YYYY">{getDateFormatDisplayName('DD MMM YYYY')}</MenuItem>
                </Select>

                <Box sx={{ backgroundColor: isDark ? '#1a2e1a' : '#e8f5e8', p: 1.5, borderRadius: 1, mt: 1 }}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                    Preview: {formatDate(new Date(), options.display.dateFormat)} {formatTime(new Date(), options.display.timeFormat)}
                  </Typography>
                </Box>
              </FormControl>

              <Divider />

              {/* Location Display Preference */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Dive Location Display
                </FormLabel>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 2 }}>
                  Choose how dive locations are displayed on dive cards
                </Typography>
                <RadioGroup
                  value={locationDisplayPreference}
                  onChange={(e) => handleLocationDisplayPreferenceChange(e.target.value)}
                >
                  <FormControlLabel
                    value="geotagged"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          📍 Geotagged Location (Default)
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Show reverse-geocoded city/area from GPS coordinates
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="description"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          📝 Suunto Description
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Show description from Suunto app (if available)
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>

                <Box sx={{ backgroundColor: isDark ? '#1a2e1a' : '#e8f5e8', p: 1.5, borderRadius: 1, mt: 2 }}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    🗺️ Location Display Hierarchy
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Priority: A manually edited dive title wins first. Otherwise the app shows your selected source (Suunto description or geotagged location), then falls back to the other source.
                  </Typography>
                </Box>
              </FormControl>

              <Divider />

              {/* Logging Level */}
              <FormControl fullWidth>
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                  Logging Level
                </FormLabel>
                <Select
                  value={logLevel}
                  onChange={(e) => handleLogLevelChange(e.target.value)}
                  size="small"
                >
                  <MenuItem value="Trace">Trace (Most Verbose)</MenuItem>
                  <MenuItem value="Debug">Debug</MenuItem>
                  <MenuItem value="Information">Information</MenuItem>
                  <MenuItem value="Warning">Warning (Recommended)</MenuItem>
                  <MenuItem value="Error">Error</MenuItem>
                  <MenuItem value="Critical">Critical (Least Verbose)</MenuItem>
                </Select>

                <Box sx={{ backgroundColor: isDark ? '#1e1e2e' : '#f5f5f5', p: 1.5, borderRadius: 1, mt: 1 }}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    {logLevel === 'Trace' && '🔍 Trace: Shows all log messages including detailed execution traces'}
                    {logLevel === 'Debug' && '🐛 Debug: Shows debug information useful for troubleshooting'}
                    {logLevel === 'Information' && 'ℹ️ Information: Shows general informational messages'}
                    {logLevel === 'Warning' && '⚠️ Warning: Shows only warnings and errors (Recommended for normal use)'}
                    {logLevel === 'Error' && '❌ Error: Shows only errors and critical issues'}
                    {logLevel === 'Critical' && '🔥 Critical: Shows only critical failures'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {logLevel === 'Warning' ? 'This is the recommended setting for most users.' : 'Lower levels produce more log output.'}
                  </Typography>
                </Box>
              </FormControl>
            </Box>
          </Paper>
        </Grid>
        )}

        {/* Equipment & Gas Certifications */}
        {activeTab === 'certifications' && (
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main'
            }}>
              <ScubaDiving /> Equipment & Gas Certifications
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Configure your tank and gas mix certifications
            </Typography>

            <Box display="flex" flexDirection="column" gap={3}>
              {/* Tank Size Selection */}
              <FormControl fullWidth>
                <InputLabel id="tank-size-label" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Default Tank Size
                </InputLabel>
                <Select
                  labelId="tank-size-label"
                  value={options.equipment.tankSize}
                  label="Default Tank Size"
                  onChange={(e) => handleTankSizeChange(e.target.value)}
                  sx={{ mb: 1 }}
                >
                  <MenuItem value="AL80">AL80 (11.0L) - Most Common</MenuItem>
                  <MenuItem value="AL63">AL63 (8.5L)</MenuItem>
                  <MenuItem value="AL100">AL100 (13.2L)</MenuItem>
                  <MenuItem value="Steel72">Steel 72 (9.6L)</MenuItem>
                  <MenuItem value="Steel100">Steel 100 (12.0L)</MenuItem>
                  <MenuItem value="Steel120">Steel 120 (15.1L)</MenuItem>
                  <MenuItem value="Steel130">Steel 130 (16.4L)</MenuItem>
                  <MenuItem value="custom">Custom Tank Size</MenuItem>
                </Select>
                
                {options.equipment.tankSize === 'custom' && (
                  <TextField
                    label="Custom Tank Volume (Liters)"
                    type="number"
                    value={options.equipment.customTankVolume || ''}
                    onChange={(e) => handleCustomTankVolumeChange(e.target.value)}
                    inputProps={{ min: 1, max: 50, step: 0.1 }}
                    sx={{ mt: 2 }}
                    helperText="Enter tank volume in liters"
                  />
                )}

                {ENABLE_GAS_ANALYTICS && (
                  <FormControl component="fieldset" sx={{ mt: 2 }}>
                    <FormLabel sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                      Gas Consumption Display
                    </FormLabel>
                    <RadioGroup
                      value={options.display.gasConsumptionMetric}
                      onChange={(e) => handleGasConsumptionMetricChange(e.target.value)}
                    >
                      <FormControlLabel
                        value="rmv"
                        control={<Radio />}
                        label="RMV - Respiratory Minute Volume (L/min or ft³/min)"
                      />
                      <FormControlLabel
                        value="sac"
                        control={<Radio />}
                        label="SAC - Surface Air Consumption (bar/min or psi/min)"
                      />
                    </RadioGroup>
                    <Typography variant="caption" color="textSecondary">
                      RMV is cylinder-independent and easier to compare across dives. SAC shows the surface-normalized pressure drop rate for your selected tank.
                    </Typography>
                  </FormControl>
                )}
              </FormControl>

              {/* Gas Mix Certifications */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Air color="primary" />
                  Gas Mix Certifications
                </Typography>
                
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.diver.gasCertifications.nitrox}
                        onChange={(e) => handleGasCertificationChange('nitrox', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={getGasCertificationDisplayName('nitrox')}
                  />
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.diver.gasCertifications.advancedNitrox}
                        onChange={(e) => handleGasCertificationChange('advancedNitrox', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={getGasCertificationDisplayName('advancedNitrox')}
                  />
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.diver.gasCertifications.trimix}
                        onChange={(e) => handleGasCertificationChange('trimix', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={getGasCertificationDisplayName('trimix')}
                  />
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.diver.gasCertifications.advancedTrimix}
                        onChange={(e) => handleGasCertificationChange('advancedTrimix', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={getGasCertificationDisplayName('advancedTrimix')}
                  />
                </FormGroup>

                {/* Gas Certifications Info */}
                {hasAnyGasCertification(options.diver.gasCertifications) && (
                  <Box sx={{ 
                    backgroundColor: isDark ? '#2d1b33' : '#f3e5f5', 
                    p: 2, 
                    borderRadius: 1, 
                    mt: 2 
                  }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                      🫧 Your Gas Mix Certifications
                    </Typography>
                    {Object.entries(options.diver.gasCertifications).map(([gasType, certified]) => 
                      certified && (
                        <Box key={gasType} sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            {getGasCertificationDisplayName(gasType as keyof AppOptions['diver']['gasCertifications'])}:
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 1 }}>
                            {getGasCertificationDescription(gasType as keyof AppOptions['diver']['gasCertifications'])}
                          </Typography>
                        </Box>
                      )
                    )}
                  </Box>
                )}
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Equipment:</strong> We're working with Suunto to get tank size data automatically. Until then, please manually select your tank size so dive details stay complete and consistent.
                </Typography>
              </Alert>

              <Box sx={{ backgroundColor: isDark ? '#1a2e1a' : '#e8f5e8', p: 2, borderRadius: 1 }}>
                <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                  🛈 Equipment & Gas Info
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  <strong>Tank Sizes:</strong><br/>
                  • AL80: Most common recreational tank<br/>
                  • Steel tanks: Higher capacity, negative buoyancy<br/><br/>
                  <strong>Gas Mix Certifications:</strong><br/>
                  • Nitrox: Extended bottom times with enriched air<br/>
                  • Trimix: Deep diving with helium-based mixes
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        )}

        {/* Certifications — paired with Equipment */}
        {activeTab === 'certifications' && (
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main'
            }}>
              🎓 Certifications
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Your scuba progression, freediving levels, and specialties — tap a section to expand it
            </Typography>

            <Box display="flex" flexDirection="column" gap={1.5}>
              {/* Total dive experience — entered by hand so divers with a long
                  history don't need to import every logged dive */}
              <CollapsibleSection
                title="🌊 Dive Experience"
                subtitle="Total dives you've done before or outside this app. Nereus uses this to gauge your experience, so you don't have to import hundreds of dives to get advice that fits you."
                defaultExpanded
              >
                <TextField
                  label="Previous dives"
                  value={previousDiveCountInput}
                  onChange={(e) => handlePreviousDiveCountChange(e.target.value)}
                  type="text"
                  inputMode="numeric"
                  size="small"
                  fullWidth
                  placeholder="e.g. 250"
                  inputProps={{ 'aria-label': 'Number of previous dives' }}
                  helperText={`Leave blank if you'd rather not say. Dives logged in the app are counted separately (max ${MAX_PREVIOUS_DIVE_COUNT.toLocaleString()}).`}
                />
              </CollapsibleSection>

              {/* Recreational / Professional Certification Levels */}
              <CollapsibleSection
                title="🤿 Scuba — Recreational / Professional"
                subtitle="Check all levels you've earned — your highest is used for personalized recommendations"
                heldCount={Object.values(options.diver.recreationalCertifications).filter(Boolean).length}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Recreational
                </Typography>
                <FormGroup sx={{ mb: 1 }}>
                  {(['OW', 'AOW', 'Rescue'] as const).map((cert) => (
                    <FormControlLabel
                      key={cert}
                      control={
                        <Checkbox
                          checked={options.diver.recreationalCertifications[cert]}
                          onChange={(e) => handleRecreationalCertificationChange(cert, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getCertificationDisplayName(cert)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getCertificationDescription(cert)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5, mt: 1 }}>
                  Professional
                </Typography>
                <FormGroup>
                  {(['DM', 'Instructor'] as const).map((cert) => (
                    <FormControlLabel
                      key={cert}
                      control={
                        <Checkbox
                          checked={options.diver.recreationalCertifications[cert]}
                          onChange={(e) => handleRecreationalCertificationChange(cert, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getCertificationDisplayName(cert)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getCertificationDescription(cert)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                {options.diver.certification !== 'none' && (
                  <Box sx={{
                    backgroundColor: isDark ? '#1a2b3d' : '#e3f2fd',
                    p: 2,
                    borderRadius: 1,
                    mt: 2
                  }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      🎓 Highest Level: {getCertificationDisplayName(options.diver.certification)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {getCertificationDescription(options.diver.certification)}
                    </Typography>
                  </Box>
                )}
              </CollapsibleSection>

              {/* Certification Card Photos — temporarily disabled.
                  Photos are stored as base64 data URLs inside customSettings, which
                  rides in a single DynamoDB attribute; a few photos approach the
                  400KB item limit and start failing every preference save. Re-enable
                  once the images live in S3 and customSettings holds only keys/URLs.
              <CollapsibleSection
                title="📇 Certification Card Photos"
                subtitle={`Keep photos of your cards handy for dive shop check-ins (up to ${MAX_CARD_PHOTOS} photos)`}
                heldCount={options.diver.certificationCardPhotos.length}
              >
                <Button
                  component="label"
                  variant="outlined"
                  color="primary"
                  startIcon={<PhotoCamera />}
                  disabled={isUploadingCardPhoto || options.diver.certificationCardPhotos.length >= MAX_CARD_PHOTOS}
                >
                  {isUploadingCardPhoto ? 'Processing…' : 'Upload Card Photo'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleCardPhotoUpload}
                  />
                </Button>

                {cardPhotoError && (
                  <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setCardPhotoError(null)}>
                    {cardPhotoError}
                  </Alert>
                )}

                {options.diver.certificationCardPhotos.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    {options.diver.certificationCardPhotos.map((photo) => (
                      <Box
                        key={photo.id}
                        sx={{
                          position: 'relative',
                          width: 160,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box
                          component="img"
                          src={photo.dataUrl}
                          alt="Certification card"
                          sx={{ width: '100%', display: 'block' }}
                        />
                        <IconButton
                          size="small"
                          aria-label="Delete card photo"
                          onClick={() => handleCardPhotoDelete(photo.id)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(0, 0, 0, 0.55)',
                            color: '#fff',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.75)' },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </CollapsibleSection>
              */}

              {/* Specialty Certifications */}
              <CollapsibleSection
                title="🏅 Scuba Specialties"
                subtitle="Select all specialty certs you hold — independent of your progression level"
                heldCount={Object.values(options.diver.specialtyCertifications).filter(Boolean).length}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Wreck
                </Typography>
                <FormGroup sx={{ mb: 1 }}>
                  {(['wreck', 'advancedWreck'] as const).map((cert) => (
                    <FormControlLabel
                      key={cert}
                      control={
                        <Checkbox
                          checked={options.diver.specialtyCertifications[cert]}
                          onChange={(e) => handleSpecialtyCertificationChange(cert, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getSpecialtyCertificationDisplayName(cert)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getSpecialtyCertificationDescription(cert)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5, mt: 1 }}>
                  Cave / Cavern
                </Typography>
                <FormGroup sx={{ mb: 1 }}>
                  {(['cavern', 'introCave', 'fullCave'] as const).map((cert) => (
                    <FormControlLabel
                      key={cert}
                      control={
                        <Checkbox
                          checked={options.diver.specialtyCertifications[cert]}
                          onChange={(e) => handleSpecialtyCertificationChange(cert, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getSpecialtyCertificationDisplayName(cert)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getSpecialtyCertificationDescription(cert)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5, mt: 1 }}>
                  Technical
                </Typography>
                <FormGroup>
                  {(['tec40', 'tec45', 'tec50', 'tecTrimix'] as const).map((cert) => (
                    <FormControlLabel
                      key={cert}
                      control={
                        <Checkbox
                          checked={options.diver.specialtyCertifications[cert]}
                          onChange={(e) => handleSpecialtyCertificationChange(cert, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getSpecialtyCertificationDisplayName(cert)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getSpecialtyCertificationDescription(cert)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                {hasAnySpecialtyCertification(options.diver.specialtyCertifications) && (
                  <Box sx={{ backgroundColor: isDark ? '#1a2b3d' : '#e3f2fd', p: 2, borderRadius: 1, mt: 2 }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                      🏅 Your Specialty Certifications
                    </Typography>
                    {getAllSpecialtyCertifications().map((cert) =>
                      options.diver.specialtyCertifications[cert] && (
                        <Box key={cert} sx={{ mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            {getSpecialtyCertificationDisplayName(cert)}
                          </Typography>
                        </Box>
                      )
                    )}
                  </Box>
                )}
              </CollapsibleSection>

              {/* NAUI Freediving (Apnea) progression — a separate track from scuba */}
              <CollapsibleSection
                title="🐬 Freediving Levels"
                subtitle="NAUI apnea progression — check every freediving level you've earned"
                heldCount={Object.values(options.diver.freediveCertifications).filter(Boolean).length}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Foundation
                </Typography>
                <FormGroup sx={{ mb: 1 }}>
                  {(['skinDiver', 'introFreediving', 'safeBuddy'] as const).map((cert) => (
                    <FormControlLabel
                      key={cert}
                      control={
                        <Checkbox
                          checked={options.diver.freediveCertifications[cert]}
                          onChange={(e) => handleFreediveCertificationChange(cert, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getFreediveCertificationDisplayName(cert)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getFreediveCertificationDescription(cert)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5, mt: 1 }}>
                  Freediver Progression
                </Typography>
                <FormGroup sx={{ mb: 1 }}>
                  {(['freediver', 'intermediateFreediver', 'advancedFreediver'] as const).map((cert) => (
                    <FormControlLabel
                      key={cert}
                      control={
                        <Checkbox
                          checked={options.diver.freediveCertifications[cert]}
                          onChange={(e) => handleFreediveCertificationChange(cert, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getFreediveCertificationDisplayName(cert)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getFreediveCertificationDescription(cert)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5, mt: 1 }}>
                  Professional
                </Typography>
                <FormGroup>
                  {(['freediverSupervisor', 'freediverInstructor'] as const).map((cert) => (
                    <FormControlLabel
                      key={cert}
                      control={
                        <Checkbox
                          checked={options.diver.freediveCertifications[cert]}
                          onChange={(e) => handleFreediveCertificationChange(cert, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getFreediveCertificationDisplayName(cert)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getFreediveCertificationDescription(cert)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                {hasAnyFreediveCertification(options.diver.freediveCertifications) && (() => {
                  const highest = getHighestFreediveCertification(options.diver.freediveCertifications);
                  if (!highest) return null;
                  const maxDepth = getFreediveCertificationMaxDepth(highest);
                  return (
                    <Box sx={{ backgroundColor: isDark ? '#1a2b3d' : '#e3f2fd', p: 2, borderRadius: 1, mt: 2 }}>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                        🐬 Highest Freediving Level: {getFreediveCertificationDisplayName(highest)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                        {getFreediveCertificationDescription(highest)}
                      </Typography>
                      {maxDepth && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          Trained depth limit: {maxDepth}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </CollapsibleSection>

              {/* NAUI Freediving specialties */}
              <CollapsibleSection
                title="🔱 Freediving Specialties"
                subtitle="Freediving specialty courses you hold — independent of your freedive level"
                heldCount={Object.values(options.diver.freediveSpecialtyCertifications).filter(Boolean).length}
              >
                <FormGroup>
                  {getAllFreediveSpecialtyCertifications().map((cert) => (
                    <FormControlLabel
                      key={cert}
                      control={
                        <Checkbox
                          checked={options.diver.freediveSpecialtyCertifications[cert]}
                          onChange={(e) => handleFreediveSpecialtyChange(cert, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getFreediveSpecialtyDisplayName(cert)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getFreediveSpecialtyDescription(cert)}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </CollapsibleSection>

              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Certifications:</strong> Your scuba progression, freediving levels, and specialty certs are used by Nereus for personalized advice, safety recommendations, and dive planning — and to avoid suggesting courses you already hold.
                </Typography>
              </Alert>
            </Box>
          </Paper>
        </Grid>
        )}

        {/* Account — profile name, email, and password */}
        {activeTab === 'account' && (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main'
            }}>
              <AccountCircle /> Account
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Update your name, email address, and password
            </Typography>

            <Box display="flex" flexDirection="column" gap={3}>
              {accountUser ? (
                <>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1, display: 'block', mb: 0.5 }}>
                      Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {accountUser.firstName} {accountUser.lastName}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1, display: 'block', mb: 0.5 }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', wordBreak: 'break-all' }}>
                      {accountUser.email}
                    </Typography>
                  </Box>

                  {accountUser.pendingEmail && (
                    <Alert severity="warning">
                      <Typography variant="body2">
                        Waiting for confirmation at <strong>{accountUser.pendingEmail}</strong>. Check that
                        inbox to finish the change — your current address still works until then.
                      </Typography>
                    </Alert>
                  )}

                  <Divider />

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ManageAccounts />}
                    onClick={() => setShowProfileDialog(true)}
                    fullWidth
                  >
                    Update Profile
                  </Button>

                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1, display: 'block', mb: 0.5 }}>
                      Welcome tour
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Skipped the setup, or want to look again? Re-run it to pick a home site, follow divers, and join a group or club.
                    </Typography>
                    <Button variant="outlined" onClick={requestOnboarding} fullWidth>
                      Restart welcome tour
                    </Button>
                  </Box>
                </>
              ) : (
                <Alert severity="info">
                  <Typography variant="body2">
                    Sign in to update your name, email address, and password.
                  </Typography>
                </Alert>
              )}
            </Box>
          </Paper>
        </Grid>
        )}

        {/* Email Notifications */}
        {activeTab === 'notifications' && (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main'
            }}>
              <Mail /> Email Notifications
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Configure email reminders for equipment service intervals
            </Typography>

            <Box display="flex" flexDirection="column" gap={3}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      Equipment Service Reminders
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Receive email notifications when equipment is due for service
                    </Typography>
                  </Box>
                  <Switch
                    checked={emailNotificationsEnabled}
                    onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
                    color="primary"
                  />
                </Box>
              </Box>

              <Divider />

              {emailNotificationsEnabled && (
                <Box>
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>
                    Remind Me (Days Before Service Due)
                  </FormLabel>
                  <Box sx={{ px: 1 }}>
                    <Slider
                      value={serviceReminderDays}
                      onChange={(_, value) => setServiceReminderDays(value as number)}
                      min={7}
                      max={90}
                      step={1}
                      marks={[
                        { value: 7, label: '7' },
                        { value: 30, label: '30' },
                        { value: 60, label: '60' },
                        { value: 90, label: '90' }
                      ]}
                      valueLabelDisplay="on"
                      valueLabelFormat={(value) => `${value} days`}
                    />
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    You'll receive an email reminder {serviceReminderDays} days before your equipment is due for service
                  </Typography>
                </Box>
              )}

              <Divider />

              <Button
                variant="contained"
                color="primary"
                startIcon={<Check />}
                onClick={handleNotificationPreferencesChange}
                fullWidth
              >
                Save Notification Preferences
              </Button>

              <Alert severity="info">
                <Typography variant="body2">
                  <strong>How it works:</strong> We'll check your equipment service intervals daily and send you a reminder email {serviceReminderDays} days before service is due.
                </Typography>
              </Alert>
            </Box>
          </Paper>
        </Grid>
        )}

        {/* Privacy & Data */}
        {activeTab === 'privacy' && (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main'
            }}>
              🔐 Privacy &amp; Data
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Control who can find you, what your buddies see, and what Nereus uses
            </Typography>

            <Box display="flex" flexDirection="column" gap={1.5}>
              {/* Discoverability — who can find this diver at all */}
              <CollapsibleSection
                title="👤 Visibility to Other Divers"
                subtitle="Whether other divers can find you and what your profile shows them"
                defaultExpanded
              >
                <FormGroup>
                  {(['discoverable', 'showEmailToOthers', 'showCertificationsPublicly', 'shareDiveMapPublicly'] as BooleanPrivacySettingKey[]).map((key) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={options.privacy[key]}
                          onChange={(e) => handlePrivacyChange(key, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getPrivacySettingDisplayName(key)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getPrivacySettingDescription(key)}
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
                    />
                  ))}
                </FormGroup>

                {!options.privacy.discoverable && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      You won't appear in buddy search. Divers you've already connected with keep their connection.
                    </Typography>
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <FormControl>
                  <FormLabel sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
                    Dive Count on My Public Profile
                  </FormLabel>
                  <RadioGroup
                    value={options.privacy.diveCountDisplay}
                    onChange={(e) => handlePrivacyChange('diveCountDisplay', e.target.value as AppOptions['privacy']['diveCountDisplay'])}
                  >
                    {DIVE_COUNT_DISPLAY_OPTIONS.map((opt) => (
                      <FormControlLabel
                        key={opt.value}
                        value={opt.value}
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.label}</Typography>
                            <Typography variant="caption" color="textSecondary">{opt.description}</Typography>
                          </Box>
                        }
                        sx={{ mb: 1, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.25 } }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                {options.privacy.diveCountDisplay === 'range' && (
                  <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                    <InputLabel id="dive-count-range-select-label">Range to Show</InputLabel>
                    <Select
                      labelId="dive-count-range-select-label"
                      label="Range to Show"
                      value={options.privacy.diveCountRangeOverride ?? '__auto__'}
                      onChange={(e) => handlePrivacyChange(
                        'diveCountRangeOverride',
                        e.target.value === '__auto__' ? null : e.target.value
                      )}
                    >
                      <MenuItem value="__auto__">
                        Automatic{ownDiveCount != null ? ` (currently ${getDiveCountRangeLabel(ownDiveCount)})` : ''}
                      </MenuItem>
                      {DIVE_COUNT_RANGE_LABELS.map((label) => (
                        <MenuItem key={label} value={label}>{label}</MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                      By default the range shown matches your real dive count. Pick a specific range here to show that one instead.
                    </Typography>
                  </FormControl>
                )}
              </CollapsibleSection>

              {/* What confirmed buddies can see */}
              <CollapsibleSection
                title="🤝 Sharing with Dive Buddies"
                subtitle="What divers you've confirmed as buddies can see about you"
              >
                <FormGroup>
                  {(['shareDiveLogWithBuddies', 'shareCertificationsWithBuddies', 'shareEquipmentWithBuddies'] as const).map((key) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={options.privacy[key]}
                          onChange={(e) => handlePrivacyChange(key, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getPrivacySettingDisplayName(key)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {getPrivacySettingDescription(key)}
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
                    />
                  ))}
                </FormGroup>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  Dives you deliberately share with a buddy are always visible to that buddy, no matter these settings.
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    Buddies can't browse each other's logs, certifications, or gear yet — today they only see
                    dives you explicitly share. Your choices here are saved and will govern that browsing when
                    it ships, so nothing is exposed before you've set it.
                  </Typography>
                </Alert>
              </CollapsibleSection>

              {/* Nereus personalization */}
              <CollapsibleSection
                title="🤿 Nereus &amp; Your Data"
                subtitle="How your AI dive instructor uses what's in your account"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={options.privacy.allowNereusPersonalization}
                      onChange={(e) => handlePrivacyChange('allowNereusPersonalization', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getPrivacySettingDisplayName('allowNereusPersonalization')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {getPrivacySettingDescription('allowNereusPersonalization')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
                />

                {!options.privacy.allowNereusPersonalization && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      Nereus won't see your dive log, certifications, or gear. His advice will be general —
                      he can't tailor depth limits or next courses to you.
                    </Typography>
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={options.privacy.allowNereusTagReplies}
                      onChange={(e) => handlePrivacyChange('allowNereusTagReplies', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getPrivacySettingDisplayName('allowNereusTagReplies')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {getPrivacySettingDescription('allowNereusTagReplies')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
                />
              </CollapsibleSection>

              {/* Export */}
              <CollapsibleSection
                title="📦 Your Data"
                subtitle="Download everything this app holds for your account"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Download />}
                  onClick={handleExportData}
                  disabled={isExporting}
                >
                  {isExporting ? 'Preparing…' : 'Export My Data (JSON)'}
                </Button>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  Includes your profile, preferences, certifications, and every dive in your log.
                </Typography>

                {exportError && (
                  <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setExportError(null)}>
                    {exportError}
                  </Alert>
                )}
              </CollapsibleSection>

              <CollapsibleSection
                title="🚫 Blocked & Muted Accounts"
                subtitle="Divers you've blocked or muted — undo either here"
              >
                <BlockedAccountsPanel />
              </CollapsibleSection>

              <Alert severity="info" icon={<Visibility />}>
                <Typography variant="body2">
                  Your dive data is yours. These settings sync to your account, so they apply everywhere you sign in.
                </Typography>
              </Alert>
            </Box>
          </Paper>
        </Grid>
        )}
        {/* Buddy & Activity Notifications — row 5 full-width */}
        {activeTab === 'notifications' && (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <People /> Buddy &amp; Activity Notifications
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Choose which events send you email alerts. The master switch above must also be enabled.
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotificationsEnabled && notifBuddyRequests}
                    disabled={!emailNotificationsEnabled}
                    onChange={async (e) => {
                      setNotifBuddyRequests(e.target.checked);
                      try {
                        const currentPrefs = preferencesManager.getPreferences();
                        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifBuddyRequests: e.target.checked } });
                      } catch { /* non-critical */ }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <People fontSize="small" color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Buddy Requests</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      Email when someone sends you a dive buddy request
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
              />

              <Divider sx={{ my: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotificationsEnabled && notifEventReminders}
                    disabled={!emailNotificationsEnabled}
                    onChange={async (e) => {
                      setNotifEventReminders(e.target.checked);
                      try {
                        const currentPrefs = preferencesManager.getPreferences();
                        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifEventReminders: e.target.checked } });
                      } catch { /* non-critical */ }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <EventIcon fontSize="small" color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Event Reminders</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      An email the day before an event you said you're going to — group, club, and community events
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
              />

              <Divider sx={{ my: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotificationsEnabled && notifComeBack}
                    disabled={!emailNotificationsEnabled}
                    onChange={async (e) => {
                      setNotifComeBack(e.target.checked);
                      try {
                        const currentPrefs = preferencesManager.getPreferences();
                        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifComeBack: e.target.checked } });
                      } catch { /* non-critical */ }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Notifications fontSize="small" color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Come-back Reminders</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      A short note, at most twice, if you've been away for a few weeks and there's activity waiting for you
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
              />

              <Divider sx={{ my: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotificationsEnabled && notifEventSuggestions}
                    disabled={!emailNotificationsEnabled}
                    onChange={async (e) => {
                      setNotifEventSuggestions(e.target.checked);
                      try {
                        const currentPrefs = preferencesManager.getPreferences();
                        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifEventSuggestions: e.target.checked } });
                      } catch { /* non-critical */ }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <EventIcon fontSize="small" color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Community Events</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      A weekly email, at most, about upcoming events in the dive communities you live in or joined
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
              />

              <Divider sx={{ my: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotificationsEnabled && notifOnboardingTips}
                    disabled={!emailNotificationsEnabled}
                    onChange={async (e) => {
                      setNotifOnboardingTips(e.target.checked);
                      try {
                        const currentPrefs = preferencesManager.getPreferences();
                        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifOnboardingTips: e.target.checked } });
                      } catch { /* non-critical */ }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Notifications fontSize="small" color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Getting-started Tips</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      A single email a few days after you sign up, if you haven't logged a dive or followed anyone yet
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
              />

              <Divider sx={{ my: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotificationsEnabled && notifWeeklyDigest}
                    disabled={!emailNotificationsEnabled}
                    onChange={async (e) => {
                      setNotifWeeklyDigest(e.target.checked);
                      try {
                        const currentPrefs = preferencesManager.getPreferences();
                        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifWeeklyDigest: e.target.checked } });
                      } catch { /* non-critical */ }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Waves fontSize="small" color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Weekly Digest</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      A Sunday email summarizing what you missed — new followers, comments, reactions, and buddy dives (only sent if there's something new)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
              />

              <Divider sx={{ my: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotificationsEnabled && notifBuddyActivity}
                    disabled={!emailNotificationsEnabled}
                    onChange={async (e) => {
                      setNotifBuddyActivity(e.target.checked);
                      try {
                        const currentPrefs = preferencesManager.getPreferences();
                        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifBuddyActivity: e.target.checked } });
                      } catch { /* non-critical */ }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Waves fontSize="small" color="info" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Buddy Activity</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      Email when a buddy logs a new dive
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
              />

              <Divider sx={{ my: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotificationsEnabled && notifDiveShares}
                    disabled={!emailNotificationsEnabled}
                    onChange={async (e) => {
                      setNotifDiveShares(e.target.checked);
                      try {
                        const currentPrefs = preferencesManager.getPreferences();
                        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifDiveShares: e.target.checked } });
                      } catch { /* non-critical */ }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ScubaDiving fontSize="small" color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Shared Dives</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      Email when a buddy shares a dive for you to add to your log
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
              />

              <Divider sx={{ my: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotificationsEnabled && notifEquipmentService}
                    disabled={!emailNotificationsEnabled}
                    onChange={async (e) => {
                      setNotifEquipmentService(e.target.checked);
                      try {
                        const currentPrefs = preferencesManager.getPreferences();
                        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifEquipmentService: e.target.checked } });
                      } catch { /* non-critical */ }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Build fontSize="small" color="warning" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Equipment Service Reminders</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      Email when equipment is due or overdue for service
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.5 } }}
              />
            </FormGroup>
          </Paper>
        </Grid>
        )}

      </Grid>

      {/* Debug & Maintenance Section */}
      {activeTab === 'account' && (
      <Box mt={4}>
        <Paper elevation={1} sx={{
          p: 3,
          backgroundColor: isDark ? '#1a2b3d' : '#e3f2fd',
          border: isDark ? '1px solid #42a5f5' : '1px solid #2196f3'
        }}>
          <Typography variant="h6" gutterBottom color="primary">
            🔧 Debug & Maintenance
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Clear cached dive location names to re-geocode all locations with updated algorithms.
          </Typography>

          <Box sx={{ mb: 2, mt: 2, p: 2, backgroundColor: isDark ? '#1e1e1e' : '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {(() => {
                const stats = geocodingService.getCacheStats();
                return `${stats.size} location${stats.size !== 1 ? 's' : ''} cached for faster loading`;
              })()}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<DeleteSweep />}
            onClick={() => {
              geocodingService.clearAllCache();
              setShowCacheClearedMessage(true);
            }}
          >
            Clear Geocoding Cache ({geocodingService.getCacheStats().size})
          </Button>

          <Typography variant="caption" display="block" sx={{ mt: 1, mb: 3, color: 'text.secondary' }}>
            After clearing, reload the page to re-geocode all dive locations
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Delete all dive data from your account to reload fresh from Suunto.
          </Typography>

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweep />}
            onClick={async () => {
              if (!window.confirm('Are you sure you want to delete ALL dives? This cannot be undone. You will need to sync from Suunto again to restore your dives.')) {
                return;
              }

              try {
                const { diveService: jwtDiveService } = await import('../services/diveService');
                const result = await jwtDiveService.deleteAllDives();
                alert(`${result.deletedCount} dives deleted successfully. Reload the page to see changes.`);
                window.location.reload();
              } catch (error) {
                console.error('Failed to delete all dives:', error);
                alert('Failed to delete dives. Please try again.');
              }
            }}
          >
            Delete All Dive Data
          </Button>

          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'error.main' }}>
            ⚠️ WARNING: This will permanently delete all dive data. Sync from Suunto to restore.
          </Typography>
        </Paper>
      </Box>
      )}

      {/* Reset Section */}
      {activeTab === 'account' && (
      <Box mt={4}>
        <Paper elevation={1} sx={{ 
          p: 3, 
          backgroundColor: isDark ? '#332614' : '#fff3e0', 
          border: isDark ? '1px solid #ffb74d' : '1px solid #ffcc02' 
        }}>
          <Typography variant="h6" gutterBottom color="warning.main">
            Reset Options
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Reset all options to their default values. This action cannot be undone.
          </Typography>
          
          {!showResetConfirm ? (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RestartAlt />}
              onClick={() => setShowResetConfirm(true)}
            >
              Reset to Defaults
            </Button>
          ) : (
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="warning"
                onClick={handleResetToDefaults}
              >
                Confirm Reset
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowResetConfirm(false)}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
      )}

      {/* Success Message */}
      <Snackbar
        open={showSavedMessage}
        autoHideDuration={2000}
        onClose={() => setShowSavedMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSavedMessage(false)} 
          severity="success" 
          sx={{ width: '100%' }}
          icon={<Check />}
        >
          Options saved successfully!
        </Alert>
      </Snackbar>

      {/* Cache Cleared Message */}
      <Snackbar
        open={showCacheClearedMessage}
        autoHideDuration={3000}
        onClose={() => setShowCacheClearedMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowCacheClearedMessage(false)} 
          severity="info" 
          sx={{ width: '100%' }}
          icon={<DeleteSweep />}
        >
          Geocoding cache cleared! Reload page to re-geocode locations.
        </Alert>
      </Snackbar>

      <UpdateProfileDialog
        open={showProfileDialog}
        onClose={() => setShowProfileDialog(false)}
        onUpdated={() => setAccountUser(authService.getUser())}
      />
    </Box>
  );
};

export default ProfileSettingsTabs;